import { type Scope } from '@sentry/core'
import { isAxiosError, type AxiosError } from 'axios'
import _find from 'lodash/find'
import _get from 'lodash/get'
import _includes from 'lodash/includes'
import _isArray from 'lodash/isArray'
import _isBoolean from 'lodash/isBoolean'
import _isNil from 'lodash/isNil'
import _isNumber from 'lodash/isNumber'
import _isObject from 'lodash/isObject'
import _isString from 'lodash/isString'
import _map from 'lodash/map'
import _mapValues from 'lodash/mapValues'
import _size from 'lodash/size'

import { getCamelCaseKey, getSnakeCaseKey } from '../json/case'

const MAX_SERIALIZE_DEPTH = 6

const REDACTED_HEADER_NAMES = new Set([
  'authorization',
  'cookie',
  'set-cookie',
  'x-csrftoken',
  'x-csrf-token',
  'x-xsrf-token',
  'x-api-key',
  'api-key',
  'proxy-authorization',
  'x-auth-token',
  'x-access-token',
  'x-refresh-token',
])

const REDACTED_FIELD_NAMES = [
  'password',
  'oldPassword',
  'newPassword1',
  'newPassword2',
  'token',
  'accessToken',
  'refreshToken',
  'secret',
  'apiKey',
  'authorization',
  'cookie',
  'sessionId',
  'sessionid',
  'csrfToken',
  'csrftoken',
] as const

const REDACTED_FIELD_LOOKUP = new Set(
  REDACTED_FIELD_NAMES.flatMap(name => [getCamelCaseKey(name), getSnakeCaseKey(name)])
)

function isRedactedFieldName(key: string): boolean {
  return (
    REDACTED_FIELD_LOOKUP.has(getCamelCaseKey(key)) ||
    REDACTED_FIELD_LOOKUP.has(getSnakeCaseKey(key))
  )
}

export function sanitizePayload(value: unknown, depth = 0): unknown {
  if (depth > MAX_SERIALIZE_DEPTH) {
    return '[Max depth exceeded]'
  }

  if (_isNil(value)) {
    return value
  }

  if (value instanceof Error) {
    return serializeForSentry(value, depth)
  }

  if (_isArray(value)) {
    return _map(value, item => sanitizePayload(item, depth + 1))
  }

  if (_isObject(value)) {
    return _mapValues(value as Record<string, unknown>, (nestedValue, key) => {
      if (isRedactedFieldName(key)) {
        return '[Redacted]'
      }
      return sanitizePayload(nestedValue, depth + 1)
    })
  }

  return serializeForSentry(value, depth)
}

export function serializeForSentry(value: unknown, depth = 0): unknown {
  if (depth > MAX_SERIALIZE_DEPTH) {
    return '[Max depth exceeded]'
  }

  if (_isNil(value)) {
    return value
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
    }
  }

  if (_isString(value) || _isNumber(value) || _isBoolean(value)) {
    return value
  }

  if (_isArray(value)) {
    return _map(value, item => serializeForSentry(item, depth + 1))
  }

  if (_isObject(value)) {
    return _mapValues(value as Record<string, unknown>, nestedValue =>
      serializeForSentry(nestedValue, depth + 1)
    )
  }

  return String(value)
}

function sanitizeHeaders(headers: unknown): unknown {
  if (!_isObject(headers)) {
    return headers
  }

  return _mapValues(headers as Record<string, unknown>, (value, key) => {
    if (REDACTED_HEADER_NAMES.has(key.toLowerCase())) {
      return '[Redacted]'
    }
    return serializeForSentry(value)
  })
}

export function isCheckoutSessionRequestUrl(url: unknown): boolean {
  return _isString(url) && _includes(url, '/checkout/') && _includes(url, '/session/')
}

export function isCheckoutSessionAxiosError(error: AxiosError): boolean {
  return isCheckoutSessionRequestUrl(_get(error, 'config.url'))
}

export function findErrorInArgs(args: unknown[]): Error | undefined {
  const found = _find(args, arg => arg instanceof Error)
  return found instanceof Error ? found : undefined
}

export function findAxiosError(error: unknown, args: unknown[] = []): AxiosError | undefined {
  if (isAxiosError(error)) {
    return error
  }

  for (const arg of args) {
    if (isAxiosError(arg)) {
      return arg
    }
  }

  return undefined
}

export function buildAxiosErrorContext(error: AxiosError): Record<string, unknown> {
  const { config, response, code } = error

  return {
    code,
    request: {
      method: config?.method,
      url: config?.url,
      baseURL: config?.baseURL,
      params: sanitizePayload(config?.params),
      data: sanitizePayload(config?.data),
      headers: sanitizeHeaders(config?.headers),
    },
    response: response
      ? {
          status: response.status,
          statusText: response.statusText,
          data: sanitizePayload(response.data),
        }
      : null,
  }
}

export function applyAxiosErrorContext(scope: Scope, error: AxiosError): void {
  const axiosContext = buildAxiosErrorContext(error)
  const request = axiosContext.request as Record<string, unknown>
  const response = axiosContext.response as Record<string, unknown> | null

  scope.setContext('axios', axiosContext)
  scope.setExtra('axiosRequestMethod', request.method)
  scope.setExtra('axiosRequestUrl', request.url)
  scope.setExtra('axiosResponseStatus', response?.status ?? null)
}

export function buildAdditionalInfoContext(args: unknown[]): Record<string, unknown> {
  const serializedArgs = _map(args, arg => sanitizePayload(arg))

  if (_size(serializedArgs) === 1) {
    const [value] = serializedArgs
    return {
      value,
      argsJson: JSON.stringify(value, null, 2),
    }
  }

  return {
    args: serializedArgs,
    argsJson: JSON.stringify(serializedArgs, null, 2),
  }
}
